import type { NavigationGuardNext } from 'vue-router'

import { useBookingCommerceStore } from '@/stores/bookingCommerce'
import { useBookingSelectionsStore } from '@/stores/bookingSelections'

import { routeName } from '@/router/routeName'

/**
 * Session Listing / Session Details: require school selected + catalog loaded.
 */
export function requireSchoolAndCatalog(
  _to: unknown,
  _from: unknown,
  next: NavigationGuardNext
): void {
  const commerceStore = useBookingCommerceStore()
  if (!commerceStore.selectedSchoolDetail || !commerceStore.isDataReady) {
    next({ name: routeName.LANDING_PAGE })
  } else {
    next()
  }
}

/**
 * Scene Selection: require session type selected.
 */
export function requireSessionType(_to: unknown, _from: unknown, next: NavigationGuardNext): void {
  const bookingSelectionsStore = useBookingSelectionsStore()
  if (!bookingSelectionsStore.hasSessionTypeSelected) {
    next({ name: routeName.SESSIONS_LISTING_PAGE })
  } else {
    next()
  }
}

/**
 * Add-Ons: require session type + scenes selected + session bundle not dirty.
 * Skips to Schedule Session if the tier has no add-ons.
 */
export function requireScenesOrSkipAddOns(
  _to: unknown,
  _from: unknown,
  next: NavigationGuardNext
): void {
  const bookingSelectionsStore = useBookingSelectionsStore()
  if (
    !bookingSelectionsStore.hasSessionTypeSelected ||
    !bookingSelectionsStore.hasValidSceneSelection ||
    bookingSelectionsStore.sessionBundleDirty
  ) {
    next({ name: routeName.SCENE_SELECTION })
    return
  }
  const commerceStore = useBookingCommerceStore()
  const key = bookingSelectionsStore.selectedSessionTypeKey
  const hasAddOns = key ? commerceStore.getAddOnsPageContent(key) !== null : false
  if (hasAddOns) {
    next()
  } else {
    next({ name: routeName.SCHEDULE_SESSION })
  }
}

/**
 * Schedule Session: require session type + session bundle not dirty.
 */
export function requireSessionTypeNotDirty(
  _to: unknown,
  _from: unknown,
  next: NavigationGuardNext
): void {
  const bookingSelectionsStore = useBookingSelectionsStore()
  if (!bookingSelectionsStore.hasSessionTypeSelected) {
    next({ name: routeName.SESSIONS_LISTING_PAGE })
  } else if (bookingSelectionsStore.sessionBundleDirty) {
    next({ name: routeName.SCENE_SELECTION })
  } else {
    next()
  }
}

/**
 * Checkout Summary: require session type + not dirty + session scheduled.
 */
export function requireSessionScheduled(
  _to: unknown,
  _from: unknown,
  next: NavigationGuardNext
): void {
  const bookingSelectionsStore = useBookingSelectionsStore()
  if (!bookingSelectionsStore.hasSessionTypeSelected) {
    next({ name: routeName.SESSIONS_LISTING_PAGE })
  } else if (bookingSelectionsStore.sessionBundleDirty) {
    next({ name: routeName.SCENE_SELECTION })
  } else if (!bookingSelectionsStore.hasSessionScheduled) {
    next({ name: routeName.SCHEDULE_SESSION })
  } else {
    next()
  }
}

/**
 * Checkout Pre-Pay: require session type + bundle reconciled + session scheduled.
 * Skips to the cart (Checkout Summary) if no prepays available.
 */
export function requireSessionTypeOrSkipPrepay(
  _to: unknown,
  _from: unknown,
  next: NavigationGuardNext
): void {
  const bookingSelectionsStore = useBookingSelectionsStore()
  if (!bookingSelectionsStore.hasSessionTypeSelected) {
    next({ name: routeName.SESSIONS_LISTING_PAGE })
    return
  }
  if (bookingSelectionsStore.sessionBundleDirty) {
    next({ name: routeName.SCENE_SELECTION })
    return
  }
  if (!bookingSelectionsStore.hasSessionScheduled) {
    next({ name: routeName.SCHEDULE_SESSION })
    return
  }
  const commerceStore = useBookingCommerceStore()
  const key = bookingSelectionsStore.selectedSessionTypeKey
  const sessionType = key ? commerceStore.sessionTypeWithContent(key) : null
  const hasPrePays = (sessionType?.prePays.length ?? 0) > 0
  if (!hasPrePays) {
    next({ name: routeName.CHECKOUT_SUMMARY })
  } else {
    next()
  }
}

/**
 * Checkout Terms: require session type + bundle reconciled + session scheduled.
 */
export function requireSessionTypeForTerms(
  _to: unknown,
  _from: unknown,
  next: NavigationGuardNext
): void {
  const bookingSelectionsStore = useBookingSelectionsStore()
  if (!bookingSelectionsStore.hasSessionTypeSelected) {
    next({ name: routeName.CHECKOUT_SUMMARY })
  } else if (bookingSelectionsStore.sessionBundleDirty) {
    next({ name: routeName.SCENE_SELECTION })
  } else if (!bookingSelectionsStore.hasSessionScheduled) {
    next({ name: routeName.SCHEDULE_SESSION })
  } else {
    next()
  }
}

/**
 * Checkout Pay: require session type + bundle reconciled + session scheduled + terms accepted.
 */
export function requireTermsAccepted(
  _to: unknown,
  _from: unknown,
  next: NavigationGuardNext
): void {
  const bookingSelectionsStore = useBookingSelectionsStore()
  if (!bookingSelectionsStore.hasSessionTypeSelected) {
    next({ name: routeName.CHECKOUT_SUMMARY })
  } else if (bookingSelectionsStore.sessionBundleDirty) {
    next({ name: routeName.SCENE_SELECTION })
  } else if (!bookingSelectionsStore.hasSessionScheduled) {
    next({ name: routeName.SCHEDULE_SESSION })
  } else if (!bookingSelectionsStore.allTermsAccepted) {
    next({ name: routeName.CHECKOUT_TERMS })
  } else {
    next()
  }
}
